import {
  AccessTime,
  ArrowForward,
  CheckCircleOutlineOutlined,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  MailOutline,
  PermDataSettingOutlined,
  TimerOffOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  Badge,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as dayjs from "dayjs";
import { useStyle } from "./styles";
import ReportFilters from "../../../../componentes/easyReport/reportFilters";
import api from "../../../../services/api";
import { ReactComponent as EmptyBox } from "../../../../assets/easyReport/iconSvg/emptyBox.svg";
import { ptBR } from "@mui/x-data-grid/locales";
import {
  EmailSendsDialog,
  findEmailConfig,
  findManyEmailSends,
  useStatusColor,
  worstStatus,
} from "../../../../componentes/easyReport/emailAgendamento";
import { useNotification } from "../../../../componentes/notificationProvider";

export interface AgendamentoRelatorio {
  id: string;
  relatorioId: string;
  agendamentoId?: string;
  ocorrencias?: string;
  dataGeracao: Date;
  dataInicialColeta: Date;
  perfil: string[];
  hosts: string[];
  items: string[];
  profiles?: string[];
  status: number;
  etapa: number;
  titulo: string | null;
}

enum IntervaloColeta {
  day = "Diário",
  week = "Semanalmente",
  month = "Mensalmente",
}

export default function AgendamentoDetalhado() {
  const [title, setTitle] = useState<string>("");
  const [rows, setRows] = useState<AgendamentoRelatorio[]>([]);
  const [filtroDataInicial, setFiltroDataInicial] =
    useState<dayjs.Dayjs | null>(null);
  const [filtroDataFinal, setFiltroDataFinal] = useState<dayjs.Dayjs | null>(
    null,
  );
  const [dataInicial, setDataInicial] = useState<dayjs.Dayjs | null>(null);
  const [dataFinal, setDataFinal] = useState<dayjs.Dayjs | null>(null);
  const [tipoColeta, setTipoColeta] = useState<string>("");
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [
    relatoriosGeradosDataUltrapassada,
    setRelatoriosGeradosDataUltrapassada,
  ] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyle(theme);
  const notify = useNotification();
  const { id } = useParams<{ id: string }>();
  const statusColor = useStatusColor();
  const [emailInfo, setEmailInfo] = useState<{
    possuiEmail: boolean;
    ativos: number;
  } | null>(null);
  const [sendsByEtapa, setSendsByEtapa] = useState<Map<number, number[]>>(
    new Map(),
  );
  const [emailRow, setEmailRow] = useState<AgendamentoRelatorio | null>(null);

  const loadEmailSends = async () => {
    if (!id) return;
    try {
      const data = await findManyEmailSends({ agendamentoId: id, porPagina: 200 });
      const map = new Map<number, number[]>();
      data.envios.forEach((e) => map.set(e.etapa, [...(map.get(e.etapa) ?? []), e.status]));
      setSendsByEtapa(map);
    } catch (error) {
      console.error("Erro ao buscar envios de e-mail:", error);
    }
  };

  useEffect(() => {
    if (!id) return;
    findEmailConfig(id)
      .then((data) =>
        setEmailInfo({
          possuiEmail: data.possuiEmail,
          ativos: (data.destinatarios ?? []).filter((d) => d.ativo).length,
        }),
      )
      .catch((error) => console.error("Erro ao buscar configuração de e-mail:", error));
    loadEmailSends();
  }, [id]);

  const columns: GridColDef<AgendamentoRelatorio>[] = [
    {
      field: "intervaloColeta",
      headerName: "Intervalo de Coleta",
      sortable: false,
      width: 240,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataInicialColeta).format("DD/MM/YYYY")}
          </Typography>
          <ArrowForward
            sx={{ fontSize: "1.1rem", color: theme.palette.text.primary }}
          />
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataGeracao).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "profiles",
      headerName: "Perfil de Relatório",
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          {Array.isArray(params.value) &&
            params.value.map((perfil) => (
              <Tooltip title={perfil}>
                <Chip
                  key={perfil}
                  label={perfil}
                  sx={{
                    ...styles.chip,
                    maxWidth: "100px",
                  }}
                />
              </Tooltip>
            ))}
        </Box>
      ),
    },
    {
      field: "hosts",
      headerName: "Hosts",
      minWidth: 200,
      flex: 2,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const value = params.value || [];
        const display = value.join(", ");
        return (
          <Tooltip title={display}>
            <Box
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {display}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "items",
      headerName: "Itens",
      minWidth: 300,
      flex: 3,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        return (
          <Box sx={styles.boxRenderCell}>
            {params.value?.map((item: string) => (
              <Tooltip title={item}>
                <Chip sx={styles.chip} label={item} key={item} />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {params.row.status === 1 ? (
            <Tooltip title="Relatório Gerado">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Relatório Gerado"
                sx={{ display: "inline-flex" }}
              >
                <CheckCircleOutlineOutlined color="success" />
              </Box>
            </Tooltip>
          ) : params.row.status === 2 ? (
            <Tooltip title="Erro ao gerar relatório">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Erro ao gerar relatório"
                sx={{ display: "inline-flex" }}
              >
                <ErrorOutlineOutlined color="error" />
              </Box>
            </Tooltip>
          ) : params.row.status === 0 ? (
            <Tooltip title="Relatório Não gerado">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Relatório Não gerado"
                sx={{ display: "inline-flex" }}
              >
                <TimerOutlined />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Relatório ultrapassou data">
              <Box
                component="span"
                tabIndex={0}
                aria-label="Relatório ultrapassou data"
                sx={{ display: "inline-flex" }}
              >
                <TimerOffOutlined sx={{ color: theme.palette.text.disabled }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "etapa",
      headerName: "Etapa",
      width: 60,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {`${params.row.etapa}/${rows.length}`}
        </Box>
      ),
    },
    {
      field: "visualizar",
      headerName: "Ações",
      headerAlign: "center",
      width: 120,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          try {
            const response = await api.post(
              "/easyReport/v2/downloadReport",
              { id: params.row.relatorioId },
              { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${params.row.titulo || "relatorio"}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Erro ao baixar relatório:", error);
            notify("Erro ao baixar relatório.", "error");
          }
        };
        const sendStatuses = sendsByEtapa.get(params.row.etapa) ?? [];
        const worst = worstStatus(sendStatuses);
        const emailAvailable =
          params.row.status === 1 &&
          (!!emailInfo?.possuiEmail || sendStatuses.length > 0);
        return (
          <Box sx={{ ...styles.boxRenderCellIcon, gap: 0.5 }}>
            <Tooltip title="Visualizar">
              <span>
                <IconButton
                  disabled={params.row.status !== 1}
                  onClick={handleClick}
                >
                  <DescriptionOutlined
                    color={params.row.status !== 1 ? "disabled" : "primary"}
                  />
                </IconButton>
              </span>
            </Tooltip>
            {(emailInfo?.possuiEmail || sendStatuses.length > 0) && (
              <Tooltip title="Envios por e-mail">
                <span>
                  <IconButton
                    disabled={!emailAvailable}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEmailRow(params.row);
                    }}
                  >
                    <Badge
                      variant="dot"
                      invisible={worst === undefined}
                      overlap="circular"
                      sx={{
                        "& .MuiBadge-dot": {
                          backgroundColor: statusColor(worst),
                          boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                        },
                      }}
                    >
                      <MailOutline
                        color={emailAvailable ? "primary" : "disabled"}
                      />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        setLoadingPage(true);
        const res = await api.post("/easyReport/v2/findSchedule", { id: id });
        setTitle(res.data.titulo);
        setTipoColeta(res.data.tipoColeta);
        setDataInicial(res.data.dataInicial);
        setDataFinal(res.data.dataFinal);
        setRows(res.data.relatorios);
        setRelatoriosGeradosDataUltrapassada(
          res.data.relatoriosGeradosDataUltrapassada,
        );
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
        notify("Erro ao carregar agendamento.", "error");
      } finally {
        setLoadingPage(false);
      }
    };
    fetchAppointment();
  }, [id]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const rowDataInicial = dayjs(row.dataInicialColeta);
      const rowDataFinal = dayjs(row.dataGeracao);

      const matchesDataInicial = filtroDataInicial
        ? rowDataInicial.isSameOrAfter(filtroDataInicial, "day")
        : true;
      const matchesDataFinal = filtroDataFinal
        ? rowDataFinal.isSameOrBefore(filtroDataFinal, "day")
        : true;

      return matchesDataInicial && matchesDataFinal;
    });
  }, [rows, filtroDataInicial, filtroDataFinal]);

  const handleClickConfiguracoes = () => {
    navigate(`/easyReportV2/agendamento/editar/${id}`);
  };
  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum relatório até o momento
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
        <Breadcrumbs
          separator={<Typography sx={styles.titleAndLinkPage}>/</Typography>}
        >
          <Button
            variant="text"
            onClick={() => navigate("/easyReportV2/agendamento")}
            sx={{
              ...styles.titleAndLinkPage,
              color: theme.palette.text.secondary,
              fontSize: 18,
              ":hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
              },
            }}
          >
            Agendamentos
          </Button>
          <Typography
            sx={{
              ...styles.titleAndLinkPage,
              color: theme.palette.text.primary,
            }}
          >
            {title || "Detalhes Agendamento"}
          </Typography>
        </Breadcrumbs>
        {/* <IconButton onClick={() => navigate("/easyReportV2/agendamento")}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{title}</Typography> */}
      </Box>
      {loadingPage ? (
        <Skeleton variant="rounded" width={"100%"} height={400} />
      ) : (
        <Paper sx={styles.paper}>
          <Box sx={styles.boxHeader}>
            <Box sx={styles.boxSearchAndFilter}>
              <Box sx={styles.boxData}>
                <Box sx={styles.boxDataHeader}>
                  <Typography
                    sx={{
                      ...styles.typography,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {dataInicial
                      ? dayjs(dataInicial).format("DD/MM/YYYY")
                      : ""}
                  </Typography>
                  <ArrowForward sx={{ fontSize: "1.1rem" }} />
                  <Typography
                    sx={{
                      ...styles.typography,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {dataFinal
                      ? dayjs(dataFinal).format("DD/MM/YYYY")
                      : ""}
                  </Typography>
                </Box>
                <Box sx={styles.boxDataHeader}>
                  <AccessTime />
                  <Typography>
                    {
                      IntervaloColeta[
                        tipoColeta as keyof typeof IntervaloColeta
                      ]
                    }
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.boxData}>
                <ReportFilters
                  filtroDataFinal={filtroDataFinal}
                  setFiltroDataFinal={setFiltroDataFinal}
                  filtroDataInicial={filtroDataInicial}
                  setFiltroDataInicial={setFiltroDataInicial}
                />
                {emailInfo && (
                  <Tooltip
                    title={
                      relatoriosGeradosDataUltrapassada
                        ? ""
                        : "Configurar envio por e-mail"
                    }
                  >
                    <Chip
                      icon={<MailOutline fontSize="small" />}
                      label={
                        emailInfo.possuiEmail
                          ? `E-mail · ${emailInfo.ativos} ${
                              emailInfo.ativos === 1
                                ? "destinatário"
                                : "destinatários"
                            }`
                          : "E-mail desativado"
                      }
                      variant={emailInfo.possuiEmail ? "filled" : "outlined"}
                      color={emailInfo.possuiEmail ? "primary" : "default"}
                      onClick={
                        relatoriosGeradosDataUltrapassada
                          ? undefined
                          : () =>
                              navigate(
                                `/easyReportV2/agendamento/editar/${id}?etapa=email`,
                              )
                      }
                      sx={{
                        height: 36,
                        ...(emailInfo.possuiEmail && {
                          color: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }),
                      }}
                    />
                  </Tooltip>
                )}
                <Button
                  onClick={handleClickConfiguracoes}
                  sx={{ gap: 1 }}
                  variant="contained"
                  disabled={relatoriosGeradosDataUltrapassada}
                >
                  <PermDataSettingOutlined />
                  Configurações
                </Button>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "65vh",
              overflow: "hidden",
              borderRadius: 2,
              border: `1px solid ${theme.palette.stroke}`,
            }}
          >
            <DataGrid
              sx={styles.dataGrid}
              rows={filteredRows}
              columns={columns}
              loading={loadingPage}
              disableColumnMenu={true}
              pageSizeOptions={[10, 25, 50, 100]}
              onPaginationModelChange={(model) => setPageSize(model.pageSize)}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0
                  ? "even-row"
                  : "odd-row"
              }
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: pageSize },
                },
              }}
            />
          </Box>
        </Paper>
      )}
      <EmailSendsDialog
        open={!!emailRow}
        onClose={() => setEmailRow(null)}
        relatorio={emailRow}
        totalEtapas={rows.length}
        emailEnabled={!!emailInfo?.possuiEmail}
        onChanged={loadEmailSends}
        onConfigure={() =>
          navigate(`/easyReportV2/agendamento/editar/${id}?etapa=email`)
        }
      />
    </>
  );
}
